import type {ChainCreditEntitlement} from "./valueChainCreditTypes";

/** The design's pack sizes in the request modal ("How many companies"). */
export const CREDIT_PACKS = [100, 200, 300] as const;

/** Credits are sold in packs of this size; a custom amount must be a multiple of it. */
export const CREDIT_PACK_STEP = 100;

/** "12 Jun 2026", the design's date style. */
export function formatCreditDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
}

/**
 * Credits spent so far. The design counts its unlocked map; the API keeps a stored balance
 * instead (§4.14), so "used" is whatever has been granted but is no longer in the balance.
 */
export function creditsUsed(entitlement: ChainCreditEntitlement): number {
    return Math.max(0, entitlement.credits_granted - entitlement.credit_balance);
}

/** Bar tone, as in the design: empty at zero, low at 10% of the entitlement or less. */
export function creditTone(entitlement: ChainCreditEntitlement): 'error' | 'warning' | 'secondary' {
    const remaining = entitlement.credit_balance;
    if (remaining === 0) return 'error';
    return remaining <= entitlement.credits_granted * 0.1 ? 'warning' : 'secondary';
}

/**
 * The design's custom-amount error: names the nearest valid pack sizes, e.g.
 * "Credits come in packs of 100. Enter 400 or 500 instead of 450."
 */
export function customCreditsError(raw: number): string {
    if (!raw) return `Enter a number of companies in multiples of ${CREDIT_PACK_STEP}.`;
    const lower = Math.max(CREDIT_PACK_STEP, Math.floor(raw / CREDIT_PACK_STEP) * CREDIT_PACK_STEP);
    const upper = Math.max(CREDIT_PACK_STEP, Math.ceil(raw / CREDIT_PACK_STEP) * CREDIT_PACK_STEP);
    const options = raw > CREDIT_PACK_STEP && raw !== lower ? `${lower} or ${upper}` : String(upper);
    return `Credits come in packs of ${CREDIT_PACK_STEP}. Enter ${options} instead of ${raw}.`;
}
